import { Badge, Input, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { FILTER_CONTROL_SIZE, FilterBar, FilterField, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import ShortText from '../../../components/ShortText'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatDateTime, formatIsoDate, useHasRole, useAppToast } from '../../../helpers'
import { ExperimentalClassService } from '../../../services/ExperimentalClass/ExperimentalClassService'
import { IExperimentalClassOptionRaw, IExperimentalClassRowRaw } from '../../../services/ExperimentalClass/types'
import { ExperimentalClassFormModal } from './ExperimentalClassFormModal'
import { ResponsibleDetailsModal } from './ResponsibleDetailsModal'

export default function ExperimentalClassesPage() {
  const service = new ExperimentalClassService()
  const toast = useAppToast()
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [rows, setRows] = useState<IExperimentalClassRowRaw[]>([])
  const [length, setLength] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [consultantFilter, setConsultantFilter] = useState('')
  const [consultants, setConsultants] = useState<IExperimentalClassOptionRaw[]>([])
  const [togglingId, setTogglingId] = useState<number | null>(null)
  const [selectedRow, setSelectedRow] = useState<IExperimentalClassRowRaw | null>(null)

  const { isOpen: formIsOpen, onOpen: formOnOpen, onClose: formOnClose } = useDisclosure()
  const { isOpen: detailsIsOpen, onOpen: detailsOnOpen, onClose: detailsOnClose } = useDisclosure()

  const hasRole = useHasRole()
  const permissions = {
    add: hasRole('add_experimental_class'),
    confirmAttendance: hasRole('confirm_experimental_class_attendance')
  }

  const loadRows = async () => {
    setIsLoading(true)
    setLoadError(false)
    try {
      const response = await service.list({
        start: (page - 1) * length,
        length,
        search,
        date: dateFilter,
        consultantId: consultantFilter
      })
      setTotalPages(Math.ceil(response.recordsFiltered / length))
      setRows(response.data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as aulas experimentais', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadRows()
  }, [page, search, length, dateFilter, consultantFilter])

  useEffect(() => {
    service.getConsultants().then(setConsultants)
  }, [])

  // O debounce da busca vive dentro do `FilterSearch`.

  const handleToggleAttended = async (row: IExperimentalClassRowRaw) => {
    setTogglingId(row.id)
    try {
      const updated = await service.markAttended(row.id, !row.attended)
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                attended: updated.attended,
                attended_at: updated.attended_at,
                attended_by_name: updated.attended_by_name
              }
            : r
        )
      )
    } catch {
      toast.error('Erro ao atualizar presença')
    } finally {
      setTogglingId(null)
    }
  }

  const openDetails = (row: IExperimentalClassRowRaw) => {
    setSelectedRow(row)
    detailsOnOpen()
  }

  return (
    <>
      <PageTitle title="Aulas Experimentais" description="Agendamentos e presença nas aulas experimentais" />

      <FilterBar
        onClear={() => {
          setSearch('')
          setDateFilter('')
          setConsultantFilter('')
          setPage(1)
        }}
        isDirty={search !== '' || dateFilter !== '' || consultantFilter !== ''}
      >
        <FilterSearch
          label="Pesquisar aluno"
          value={search}
          onChange={(value) => {
            setPage(1)
            setSearch(value)
          }}
          placeholder="Buscar aluno"
        />
        <FilterField label="Data da aula">
          <Input
            type="date"
            size={FILTER_CONTROL_SIZE}
            value={dateFilter}
            onChange={(e) => {
              setPage(1)
              setDateFilter(e.target.value)
            }}
          />
        </FilterField>
        <FilterSelect
          label="Consultor"
          value={consultantFilter}
          onChange={(value) => {
            setPage(1)
            setConsultantFilter(value)
          }}
          options={consultants.map((consultant) => ({ value: consultant.id, label: consultant.name }))}
        />
      </FilterBar>

      <TableContainer padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th>Data</Th>
              <Th>Horário</Th>
              <Th>Dia da semana</Th>
              <Th>Aluno</Th>
              <Th>Curso</Th>
              <Th>Professor</Th>
              <Th>Consultor</Th>
              <Th>Compareceu</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <TableSkeletonRows cols={9} />
            ) : loadError ? (
              <TableErrorRow colSpan={9} entity="aulas experimentais" feminine onRetry={loadRows} />
            ) : rows.length === 0 ? (
              <TableEmptyRow colSpan={9} entity="aula experimental" feminine />
            ) : (
              rows.map((row) => (
                <Tr key={row.id}>
                  <Td>{formatIsoDate(row.class_date)}</Td>
                  <Td>{row.class_time.replace(':', 'h')}</Td>
                  <Td>{row.day_of_week}</Td>
                  <Td>
                    <ShortText text={row.student_name} beforeChars={20} afterChars={0} /> ({row.student_age})
                  </Td>
                  <Td>
                    {row.course_name ? <ShortText text={row.course_name} beforeChars={30} afterChars={0} /> : null}
                  </Td>
                  <Td>
                    {row.teacher_name ? <ShortText text={row.teacher_name} beforeChars={20} afterChars={0} /> : null}
                  </Td>
                  <Td>
                    {row.consultant_name ? (
                      <ShortText text={row.consultant_name} beforeChars={20} afterChars={0} />
                    ) : null}
                  </Td>
                  <Td>
                    <Badge colorScheme={row.attended ? 'green' : 'gray'}>
                      {row.attended ? 'Compareceu' : 'Aguardando'}
                    </Badge>
                    {row.attended && row.attended_by_name && (
                      <Text fontSize="0.7em" color="gray.600" mt={0.5} whiteSpace="normal">
                        por <ShortText text={row.attended_by_name} beforeChars={10} afterChars={0} />
                        {row.attended_at && <> em {formatDateTime(row.attended_at)}</>}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <ActionButton action="view" onClick={() => openDetails(row)} />
                    {permissions.confirmAttendance && (
                      <ActionButton
                        action={row.attended ? 'reject' : 'approve'}
                        label={row.attended ? 'Desmarcar presença' : 'Marcar presença'}
                        colorScheme={row.attended ? 'gray' : 'green'}
                        variant={row.attended ? 'outline' : 'solid'}
                        icon={<Icon name={row.attended ? 'lni lni-close' : 'lni lni-checkmark-circle'} />}
                        isLoading={togglingId === row.id}
                        onClick={() => handleToggleAttended(row)}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(p: number) => setPage(p)}
        pageSize={length}
        onPageSizeChange={(pageSize) => {
          setLength(pageSize)
          setPage(1)
        }}
      />

      {permissions.add && <ActionFab label="Nova aula experimental" onClick={formOnOpen} />}

      <ExperimentalClassFormModal isOpen={formIsOpen} onClose={formOnClose} onCreated={loadRows} />
      <ResponsibleDetailsModal isOpen={detailsIsOpen} onClose={detailsOnClose} row={selectedRow} />
    </>
  )
}
